import * as educationSelectors from 'education-tools/src/selectors'
import Keycloak from 'keycloak-js'
import * as commonSelectors from 'mage-common/src/selectors'
import * as configSelectors from 'mage-config/src/selectors'
import * as conversationSelectors from 'mage-conversation/src/selectors'
import * as coreSelectors from 'mage-core/src/selectors'
import { memoizeWithTokenHelper } from 'mage-deps/@mpp/token-helper'
import * as i18nSelectors from 'mage-i18n/src/selectors'
import * as R from 'ramda'
import { createSelector } from 'reselect'

import { freshChat as fcConfig } from './config'

const isNkoApp = R.pathEq(['config', 'isNkoApp'], true)

const makeNkoUrl = (nkoConfig, estoreId) => app => ({
  ...app,
  url: nkoConfig.baseUrl + R.pathOr('', ['url'], app).replace('{estoreId}', estoreId),
  newTab: true
})

const urlWithFinalSlash = url => url.endsWith('/') ? url : `${url}/`

const addRealmFragment = (realm, url) => `${urlWithFinalSlash(url)}#realm=${realm}`

const makeRealmPreservingUrl = realm => app => ({
  ...app,
  url: addRealmFragment(realm, app.url)
})

const appRequiresNkoAdmin = R.pathEq(['config', 'nkoRole'], 'admin')

const hasCategory = R.complement(R.pathEq(['category'], ''))

const appLink = clientId => (apps, realm) => apps[clientId] && addRealmFragment(realm, apps[clientId].url)

/** SELECTORS */
const getLanguages = createSelector(
  i18nSelectors.translations,
  coreSelectors.clientId,
  (translations, clientId) => R.pipe(
    R.pickBy(R.has(clientId)),
    R.keys
  )(translations)
)

const freshChatConfig = createSelector(
  configSelectors.environment,
  configSelectors.region,
  fcConfig
)

const channels = createSelector(
  freshChatConfig,
  conversationSelectors.markets,
  (freshChat, markets) => {
    const channels = []
    if (markets && freshChat.tags) {
      freshChat.tags.forEach(tag => {
        markets.forEach(market => {
          channels.push(tag.replace('{country}', market.toLowerCase()))
        })
      })
    }
    return channels
  }
)

const getAppConfig = createSelector(
  configSelectors.apps,
  coreSelectors.clientId,
  (apps, clientId) => apps[clientId]
)

const getAppBaseUrl = createSelector(
  configSelectors.getMerchantPortalBaseUrl,
  coreSelectors.getBasename,
  (merchantPortalBaseUrl, basename) => `${merchantPortalBaseUrl}${basename}`
)

const getHomeLink = createSelector(
  configSelectors.apps,
  coreSelectors.keycloakRealm,
  appLink('home-fe')
)

const getAppLink = createSelector(
  configSelectors.apps,
  coreSelectors.keycloakRealm,
  coreSelectors.clientId,
  (apps, realm, clientId) => appLink(clientId)(apps, realm)
)

const keycloakConfig = createSelector(
  configSelectors.keycloak,
  coreSelectors.keycloakRealm,
  getAppConfig,
  coreSelectors.isPartner,
  (keycloakConfig, realm, appConfig, isPartner) => {
    let clientId = keycloakConfig.clientId
    if (isPartner) {
      clientId = keycloakConfig.partnerClientId
    } else if (appConfig && appConfig.keycloakClient) {
      clientId = appConfig.keycloakClient
    }
    return {
      ...R.omit(['realms'], keycloakConfig),
      ...R.pathOr({}, ['realms', realm], keycloakConfig),
      realm,
      clientId
    }
  }
)

const getKeycloak = createSelector(
  keycloakConfig,
  keycloakConfig => {
    const config = R.pick(['url', 'realm', 'clientId'], keycloakConfig)
    return new Keycloak(config)
  }
)

const getApps = createSelector(
  configSelectors.apps,
  coreSelectors.keycloakRealm,
  configSelectors.nko,
  educationSelectors.educationFeatures,
  (apps, realm, nkoConfig, educationFeatures) => memoizeWithTokenHelper(
    tokenHelper => {
      const tokenMids = tokenHelper.getMids()
      const estoreId = tokenHelper.getEids()[0]
      return R.pipe(
        R.toPairs,
        R.map(([appName, appProps]) => ({ clientId: appName, ...appProps })),
        R.filter(app => !app.educationFeature || R.prop(app.educationFeature, educationFeatures)),
        R.filter(R.prop('visibleInMenu')),
        R.filter(app => app.mids ? !R.isEmpty(R.intersection(app.mids, tokenMids)) : true),
        R.sortBy(R.prop('sortOrder')),
        R.map(
          R.ifElse(
            isNkoApp,
            makeNkoUrl(nkoConfig, estoreId),
            makeRealmPreservingUrl(realm)
          )
        )
      )(apps)
    }
  )
)

const getUserAccessibleApps = createSelector(
  getApps,
  apps => memoizeWithTokenHelper(
    tokenHelper => {
      const clientIds = tokenHelper.getClientsForAllEntities()
      const isNkoAdmin = commonSelectors.tokenHelper.isNkoAdmin(tokenHelper)
      const isNkoUser = commonSelectors.tokenHelper.isNkoUser(tokenHelper)
      return apps(tokenHelper)
        .filter(app => app.isStaticPage || clientIds.includes(app.clientId))
        .filter(app => !isNkoUser || !appRequiresNkoAdmin(app) || isNkoAdmin)
    }
  )
)

const getAppCategories = createSelector(
  configSelectors.appCategories,
  (categories) => R.pipe(
    R.toPairs,
    R.map(([categoryKey, value]) => ({ id: categoryKey, ...value })),
    R.sortBy(R.prop('order'))
  )(categories)
)

const getCategorizedApps = createSelector(
  getAppCategories,
  getUserAccessibleApps,
  (categories, apps) => memoizeWithTokenHelper(
    tokenHelper => {
      return R.pipe(
        R.filter(hasCategory),
        R.sort((appA, appB) => {
          const categoryTitles = R.map(({ title }) => title, categories)
          return categoryTitles.indexOf(appA.category) - categoryTitles.indexOf(appB.category)
        }),
        R.groupBy(app => app.category)
      )(apps(tokenHelper))
    }
  )
)

const cmsLocale = createSelector(
  i18nSelectors.language,
  language => ({ no: 'nb' }[language] || language)
)

const cmsEnvironment = createSelector(
  configSelectors.environment,
  environment =>
    ({
      production: 'master',
      playground: 'master'
    }[environment] || 'staging')
)

export {
  getLanguages,
  freshChatConfig,
  channels,
  getAppConfig,
  getAppBaseUrl,
  getHomeLink,
  getAppLink,
  keycloakConfig,
  getKeycloak,
  getApps,
  getUserAccessibleApps,
  getCategorizedApps,
  getAppCategories,
  cmsLocale,
  cmsEnvironment
}
